import { z } from 'zod';
import { optionalPhone, postcode } from './organisations.js';

const optionalText = (max: number) =>
  z.string().trim().max(max, `Use at most ${max} characters`).nullish().transform((v) => (v ? v : null));

const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date')
  .nullish()
  .or(z.literal(''))
  .transform((v) => (v ? v : null));

const email = z.string().trim().toLowerCase().min(1, 'Enter an email address').email('Enter a valid email address').max(254);

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Enter a valid email address')
  .nullish()
  .transform((v) => (v ? v : null));

/**
 * An RFID card's tag, as the charger reports it (OCPP idTag: at most 20 characters).
 * Spaces and colons people copy from a card reader app are removed, and letters are stored
 * in capitals (chargers' tags are compared ignoring case).
 */
export const cardTag = z
  .string()
  .transform((v) => v.replace(/[\s:]/g, '').toUpperCase())
  .pipe(
    z
      .string()
      .min(1, 'Enter the card number')
      .max(20, 'Card numbers have at most 20 characters')
      .regex(/^[\x21-\x7e]+$/, 'Letters and numbers only')
      .refine((v) => !/^(PUB-|ADM-|DIRECT-)/i.test(v), 'This is a Pulsepoint tag, not a card'),
  );

/** $/kWh, blank for none. */
const rate = z.preprocess(
  (v) => (v === '' || v === undefined ? null : typeof v === 'string' ? v.trim().replace(/^\$\s*/, '') : v),
  z.coerce.number({ invalid_type_error: 'Enter a rate in dollars, e.g. 0.30' }).min(0, 'Must be 0 or more').max(5, 'That looks too high: rates are in dollars per kWh').nullable(),
);

export const cardBody = z.object({
  tag: cardTag,
  label: optionalText(64),
  expiresOn: optionalDate,
  /** Admins: $/kWh this card is charged instead of the charger's private price (0 for free). Blank: the private price. */
  ratePerKwh: rate.optional(),
});
export type CardBody = z.input<typeof cardBody>;

export const cardUpdateBody = z
  .object({
    label: optionalText(64),
    expiresOn: optionalDate,
    isActive: z.boolean(),
    ratePerKwh: rate,
  })
  .partial();

/** What a driver fills in themselves (onboarding link) and staff can edit. */
const personal = {
  name: z.string().trim().min(1, 'Enter their name').max(128),
  email,
  phone: optionalPhone,
  postcode,
  vehicleType: optionalText(128),
  registration: z
    .string()
    .trim()
    .toUpperCase()
    .max(16, 'At most 16 characters')
    .nullish()
    .transform((v) => (v ? v : null)),
};

export const driverBody = z.object({
  organisationId: z.string().uuid('Choose an organisation'),
  ...personal,
  onboardedOn: optionalDate,
  powersensorEmail: optionalEmail,
  evSensorNumber: z.coerce.number().int().min(1).max(3).default(2),
  serialWifiPlug: optionalText(64),
  serialSensorDistribution: optionalText(64),
  serialSensorCharger: optionalText(64),
  notes: optionalText(2000),
});
export type DriverBody = z.input<typeof driverBody>;

export const driverCreateBody = driverBody.extend({
  /** Cards to add straight away. */
  cards: z.array(cardBody).max(10).default([]),
});

export const driverUpdateBody = driverBody.partial().extend({ isActive: z.boolean().optional() });

/** Monthly Stripe billing set-up for a driver (admins). Invoices come with the Stripe stage. */
export const driverBillingBody = z.object({
  isBillable: z.boolean(),
  dayOfMonth: z.coerce.number().int().min(1, 'Between 1 and 28').max(28, 'Between 1 and 28'),
  stripeCustomerEmail: optionalEmail,
});
export type DriverBillingBody = z.input<typeof driverBillingBody>;

export const odometerBody = z.object({
  km: z.coerce.number({ invalid_type_error: 'Enter the reading in km' }).int('Whole km').min(0).max(2_000_000),
  recordedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date'),
});

export const onboardingLinkBody = z.object({
  organisationId: z.string().uuid('Choose an organisation'),
  label: optionalText(64),
  /** How long the link works. */
  days: z.coerce.number().int().min(1, 'At least 1 day').max(90, 'At most 90 days').default(30),
});

/** What a driver sends from the onboarding page. `website` is a trap for bots: people never see it. */
export const publicDriverBody = z.object({
  ...personal,
  cards: z.array(cardBody.pick({ tag: true, label: true })).max(5, 'At most 5 cards').default([]),
  website: z.string().max(500).optional(),
});
export type PublicDriverBody = z.input<typeof publicDriverBody>;
